import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  CircleX, 
  QrCode, 
  CheckCircle2, 
  XCircle, 
  User,
  Ticket,
  Clock,
  AlertCircle,
  Search
} from "lucide-react";
import clsx from "clsx";
import { Html5Qrcode } from "html5-qrcode";
import "./LectorQR.css";
import qrAdminService from "../../services/qrAdminService";
import InlineSpinner from "../ui/InlineSpinner";

export default function LectorQRAdmin({ onClose, evento }) {
  const [loading, setLoading] = useState(true);
  const [invitados, setInvitados] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [scanning, setScanning] = useState(false);
  const [lastScan, setLastScan] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [boletosConfirmar, setBoletosConfirmar] = useState(1);
  const [procesandoEscaneo, setProcesandoEscaneo] = useState(false);
  const [boletosRegistrados, setBoletosRegistrados] = useState({});
  const [cameraActive, setCameraActive] = useState(true);
  
  const html5QrCodeRef = useRef(null);
  const scannerIdRef = useRef("qr-reader-admin");
  const lastScanTextRef = useRef(null);
  const scanTimeoutRef = useRef(null);
  const scannerSessionRef = useRef(0);
  const cameraActiveRef = useRef(cameraActive);
  const invitadosRef = useRef(invitados);
  const pendingConfirmationRef = useRef(pendingConfirmation);
  const procesandoEscaneoRef = useRef(procesandoEscaneo);
  const eventoRef = useRef(evento);

  invitadosRef.current = invitados;
  pendingConfirmationRef.current = pendingConfirmation;
  procesandoEscaneoRef.current = procesandoEscaneo;
  eventoRef.current = evento;

  useEffect(() => {
    cameraActiveRef.current = cameraActive;
  }, [cameraActive]);

  // Solo como último recurso (p. ej. start() interrumpido). Evita onabort de html5-qrcode.
  const releaseMediaTracks = useCallback(() => {
    const root = document.getElementById(scannerIdRef.current);
    if (!root) return;
    root.querySelectorAll("video").forEach((video) => {
      video.onabort = null;
      video.onerror = null;
      try {
        video.pause();
      } catch {
        // ignore
      }
      const stream = video.srcObject;
      if (stream && typeof stream.getTracks === "function") {
        stream.getTracks().forEach((track) => {
          if (track.readyState !== "ended") track.stop();
        });
      }
      video.srcObject = null;
    });
  }, []);

  const releaseCameraHardware = useCallback(async () => {
    const scanner = html5QrCodeRef.current;
    let stoppedCleanly = false;

    if (scanner) {
      try {
        const state = scanner.getState();
        // 2 = SCANNING, 3 = PAUSED — stop() cierra el video sin disparar onabort
        if (state === 2 || state === 3) {
          await scanner.stop();
          stoppedCleanly = true;
        }
      } catch {
        // Aún no terminó de iniciar o ya detenido
      }
      try {
        await scanner.clear();
      } catch {
        // ignore
      }
      html5QrCodeRef.current = null;
    }

    if (!stoppedCleanly) {
      releaseMediaTracks();
    }
    setScanning(false);
  }, [releaseMediaTracks]);

  const stopScannerInstance = useCallback(async () => {
    scannerSessionRef.current += 1;
    await releaseCameraHardware();
  }, [releaseCameraHardware]);

  // Cargar lista de invitados al abrir
  const cargarInvitados = useCallback(async () => {
    if (!evento?.id) return;
    setLoading(true);
    const res = await qrAdminService.obtenerInvitadosQR(evento.id);
    if (res.success) {
      setInvitados(res.data.invitados || []);
    }
    setLoading(false);
  }, [evento]);

  useEffect(() => {
    cargarInvitados();
  }, [cargarInvitados]);

  // Callback estable: no reinicia el escáner cuando cargan invitados o cambia el estado UI
  const onScanSuccess = useCallback(async (decodedText, decodedResult) => {
    const invitados = invitadosRef.current;
    const pendingConfirmation = pendingConfirmationRef.current;
    const procesandoEscaneo = procesandoEscaneoRef.current;
    const evento = eventoRef.current;

    // Evitar escaneos duplicados - ignorar si es el mismo código en los últimos 2 segundos o si está procesando
    if (lastScanTextRef.current === decodedText || pendingConfirmation || procesandoEscaneo) {
      return;
    }

    console.log('✅ QR Code detectado:', decodedText);
    lastScanTextRef.current = decodedText;
    
    if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
    scanTimeoutRef.current = setTimeout(() => {
      lastScanTextRef.current = null;
    }, 2000);
    
    try {
      // Formato real del QR: "Nombre | Evento | N boletos | Mesas: X,Y | correo@mail.com"
      // Intentar primero el formato de texto plano (pipe-separated)
      let correoEscaneado = null;
      let invitadoData = null;
      let mesasQR = null;
      let boletosQR = null;

      // Detectar si contiene pipes (formato actual de los boletos)
      if (decodedText.includes(' | ')) {
        const partes = decodedText.split(' | ').map(p => p.trim());
        // El correo siempre es el último elemento
        const posibleCorreo = partes[partes.length - 1];
        if (posibleCorreo.includes('@')) {
          correoEscaneado = posibleCorreo.toLowerCase();
          console.log('📧 Correo extraído del QR:', correoEscaneado);
        }

        // Buscar parte de mesas y boletos en las partes intermedias
        partes.forEach((parte) => {
          const lower = parte.toLowerCase();
          if (lower.includes('mesa')) {
            // ej: "Mesas: A-10" o "Mesa: 5"
            mesasQR = parte.replace(/^(mesas?:?\s*)/i, '').trim();
          } else if (lower.includes('boleto')) {
            // ej: "2 boletos" o "1 boleto"
            const match = parte.match(/(\d+)/);
            if (match) {
              boletosQR = parseInt(match[1]);
            }
          }
        });
      } else {
        // Intentar parsear como JSON (formato compacto)
        try {
          const qrData = JSON.parse(decodedText);
          const invitadoIdEscaneado = qrData.i || qrData.invitado_id;
          const eventoIdEscaneado = qrData.e || qrData.evento_id;

          if (eventoIdEscaneado && evento?.id && eventoIdEscaneado !== evento.id) {
            throw new Error(`Este boleto pertenece a otro evento.`);
          }

          if (invitadoIdEscaneado) {
            invitadoData = invitados.find(inv =>
              inv.invitado_id === invitadoIdEscaneado ||
              inv.id === invitadoIdEscaneado
            );
          }
        } catch (jsonErr) {
          // Si el texto contiene @, intentar usarlo como correo directamente
          if (decodedText.includes('@')) {
            correoEscaneado = decodedText.trim().toLowerCase();
          }
        }
      }

      // Buscar por correo si no se encontró por ID
      if (!invitadoData && correoEscaneado) {
        invitadoData = invitados.find(inv =>
          inv.correo && inv.correo.toLowerCase() === correoEscaneado
        );
      }

      if (!invitadoData) {
        const identificador = correoEscaneado || decodedText.substring(0, 60);
        throw new Error(`No se encontró un invitado confirmado. Verificar: ${identificador}`);
      }
      
      console.log('✅ Información del invitado encontrada:', invitadoData);
      
      // Calcular boletos restantes (total - ya usados)
      const boletosYaUsados = invitadoData.boletos_escaneados || 0;
      const boletosRestantes = invitadoData.cantidad_boletos - boletosYaUsados;
      
      if (boletosRestantes <= 0) {
        const scanData = {
          id: `QR-${Date.now()}`,
          timestamp: new Date().toISOString(),
          invitadoId: invitadoData.invitado_id,
          invitado: invitadoData.invitado_nombre,
          boletos: 0,
          boletosTotal: invitadoData.cantidad_boletos,
          boletosUsados: boletosYaUsados,
          valido: false,
          evento: evento?.nombre_evento || 'Evento',
          error: 'Todos los boletos ya fueron utilizados'
        };
        
        setLastScan(scanData);
        setScanHistory(prev => [scanData, ...prev].slice(0, 10));
        return;
      }

      // Obtener mesas asignadas de la base de datos si no vienen del QR
      const mesasDb = invitadoData.mesas && Array.isArray(invitadoData.mesas)
        ? invitadoData.mesas.map(m => m.mesa_numero).join(', ')
        : '';
      const mesasFinal = mesasQR || mesasDb || '';
      
      setPendingConfirmation({
        invitadoData: invitadoData,
        boletosRestantes: boletosRestantes,
        boletosYaUsados: boletosYaUsados,
        mesas: mesasFinal || 'Sin asignar'
      });
      setBoletosConfirmar(Math.min(boletosRestantes, 1));
      
    } catch (error) {
      console.error('❌ Error al procesar código QR:', error);
      const errorScanData = {
        id: `QR-${Date.now()}`,
        timestamp: new Date().toISOString(),
        invitadoId: decodedText,
        invitado: 'Error al validar QR',
        boletos: 0,
        valido: false,
        evento: evento?.nombre_evento || 'Evento',
        error: error.message || 'El código QR no es válido para este evento'
      };
      
      setLastScan(errorScanData);
      setScanHistory(prev => [errorScanData, ...prev].slice(0, 10));
    }
  }, []);

  const onScanError = useCallback(() => {
    // Errores de frame (cuando no hay QR a la vista) son ignorados
  }, []);

  // Inicializar escáner
  useEffect(() => {
    let isActive = true;
    const session = ++scannerSessionRef.current;

    const shouldContinue = () =>
      isActive &&
      session === scannerSessionRef.current &&
      cameraActiveRef.current;

    const startScanner = async () => {
      try {
      if (!shouldContinue()) return;

      if (!document.getElementById(scannerIdRef.current)) {
        console.warn('DOM element for scanner not found');
        return;
      }

        // Limpiar instancia anterior si existe
        if (html5QrCodeRef.current) {
          await releaseCameraHardware();
          if (!shouldContinue()) return;
        }

        html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);

        // Configuración optimizada para detección automática y rápida de QR
        // aspectRatio: 1 = cuadrado (portrait-friendly), evita que el usuario tenga que rotar el QR
        const config = {
          fps: 15,
          aspectRatio: 1, // Cuadrado: funciona en orientación normal (portrait)
          disableFlip: false,
          videoConstraints: {
            width: { min: 480, ideal: 720, max: 1280 },
            height: { min: 480, ideal: 720, max: 1280 }
          },
          formatsToSupport: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16],
          experimentalFeatures: {
            useBarCodeDetectorIfSupported: true // Usa API nativa móvil para escaneo ultra rápido
          }
        };

        let scannerStarted = false;

        // Estrategia 1: Cámara trasera (environment)
        if (!scannerStarted) {
          try {
            console.log('🔄 Intentando iniciar con cámara trasera...');
            await html5QrCodeRef.current.start(
              { facingMode: "environment" },
              config,
              onScanSuccess,
              onScanError
            );
            if (!shouldContinue()) {
              await releaseCameraHardware();
              return;
            }
            console.log('✓ Escáner iniciado con cámara trasera');
            scannerStarted = true;
          } catch (error) {
            console.log('✗ Cámara trasera no disponible:', error.message);
          }
        }

        // Estrategia 2: Cámara frontal (user)
        if (!scannerStarted) {
          try {
            console.log('🔄 Intentando iniciar con cámara frontal...');
            if (html5QrCodeRef.current) {
              await releaseCameraHardware();
              if (!shouldContinue()) return;
            }
            html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);
            await html5QrCodeRef.current.start(
              { facingMode: "user" },
              config,
              onScanSuccess,
              onScanError
            );
            if (!shouldContinue()) {
              await releaseCameraHardware();
              return;
            }
            console.log('✓ Escáner iniciado con cámara frontal');
            scannerStarted = true;
          } catch (error) {
            console.log('✗ Cámara frontal no disponible:', error.message);
          }
        }

        // Estrategia 3: Cualquier cámara disponible por ID
        if (!scannerStarted) {
          try {
            console.log('🔄 Buscando cámaras por ID...');
            if (html5QrCodeRef.current) {
              await releaseCameraHardware();
              if (!shouldContinue()) return;
            }
            const devices = await Html5Qrcode.getCameras();
            console.log('📷 Cámaras encontradas:', devices.length, devices);
            if (devices && devices.length > 0) {
              html5QrCodeRef.current = new Html5Qrcode(scannerIdRef.current);
              const cameraId = devices[0].id;
              await html5QrCodeRef.current.start(
                cameraId,
                config,
                onScanSuccess,
                onScanError
              );
              if (!shouldContinue()) {
                await releaseCameraHardware();
                return;
              }
              console.log('✓ Escáner iniciado con cámara:', devices[0].label || cameraId);
              scannerStarted = true;
            } else {
              throw new Error('No se encontraron cámaras disponibles');
            }
          } catch (error) {
            console.log('✗ Error al usar cámara por ID:', error.message);
            throw error;
          }
        }

        if (!scannerStarted) {
          throw new Error('No se pudo iniciar el escáner con ninguna estrategia');
        }

        if (shouldContinue()) setScanning(true);

      } catch (err) {
        console.error("❌ Error al inicializar el escáner:", err);
        if (shouldContinue()) setScanning(false);
        await releaseCameraHardware();
      }
    };

    let initTimeout;
    // Dar tiempo para que el DOM se renderice y se cargue la info
    if (!loading && cameraActive) {
      initTimeout = setTimeout(startScanner, 300);
    }

    return () => {
      isActive = false;
      scannerSessionRef.current += 1;
      if (initTimeout) clearTimeout(initTimeout);
      if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current);
      void releaseCameraHardware();
    };
  }, [loading, cameraActive, releaseCameraHardware]);

  const toggleCamera = async () => {
    if (cameraActive) {
      cameraActiveRef.current = false;
      await stopScannerInstance();
      setCameraActive(false);
    } else {
      setCameraActive(true); // useEffect will trigger startScanner
    }
  };

  const handleClose = async () => {
    cameraActiveRef.current = false;
    await stopScannerInstance();
    onClose?.();
  };

  const handleCancelarConfirmacion = () => {
    setPendingConfirmation(null);
    lastScanTextRef.current = null;
  };

  const handleConfirmarBoletos = async () => {
    if (!pendingConfirmation) return;
    
    setProcesandoEscaneo(true);
    const { invitadoData } = pendingConfirmation;
    
    try {
      const res = await qrAdminService.registrarEscaneoQR(
        evento.id,
        invitadoData.invitado_id,
        boletosConfirmar
      );

      if (res.success) {
        const scanData = {
          id: `QR-${Date.now()}`,
          timestamp: new Date().toISOString(),
          invitadoId: invitadoData.invitado_id,
          invitado: invitadoData.invitado_nombre,
          boletos: boletosConfirmar,
          boletosTotal: invitadoData.cantidad_boletos,
          boletosUsados: (invitadoData.boletos_escaneados || 0) + boletosConfirmar,
          valido: true,
          evento: evento?.nombre_evento || 'Evento',
          mesas: pendingConfirmation.mesas || ''
        };

        setLastScan(scanData);
        setScanHistory(prev => [scanData, ...prev].slice(0, 10));
        
        // Actualizar la lista local
        setInvitados(prev => prev.map(inv => {
          if (inv.invitado_id === invitadoData.invitado_id) {
            return {
              ...inv,
              boletos_escaneados: (inv.boletos_escaneados || 0) + boletosConfirmar
            };
          }
          return inv;
        }));

        setPendingConfirmation(null);
        setTimeout(() => { lastScanTextRef.current = null; }, 500);
      } else {
        throw new Error(res.error);
      }
    } catch (error) {
      alert("Error al registrar: " + error.message);
    } finally {
      setProcesandoEscaneo(false);
    }
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const renderStatusIcon = (isValid, boletosRestantes) => {
    if (!isValid) {
      return (
        <div className="w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center animate-bounce-short">
          <XCircle className="w-10 h-10 text-red-600 dark:text-red-400" />
        </div>
      );
    }
    return (
      <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center animate-bounce-short">
        <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6 bg-black/50 dark:bg-black/80 backdrop-blur-sm">
        <div className="bg-white dark:bg-[#1e1e1e] p-8 rounded-2xl flex flex-col items-center">
          <InlineSpinner size="md" />
          <p className="mt-4 text-gray-700 dark:text-gray-300">Cargando invitados del evento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-2 sm:p-4 bg-black/50 dark:bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-[#121212] w-full max-w-6xl h-full sm:h-[90vh] rounded-none sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="flex-shrink-0 bg-gradient-to-r from-casal to-[#1a5b66] dark:from-[#1a1a1a] dark:to-[#222] px-6 py-4 flex items-center justify-between border-b border-casal/20 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-lg backdrop-blur-sm">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white tracking-wide">
                Lector QR - Administrador
              </h2>
              <p className="text-sm text-casal-100 dark:text-gray-400 font-medium opacity-90">
                {evento?.nombre_evento || 'Evento'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-full transition-all"
            aria-label="Cerrar"
          >
            <CircleX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col lg:flex-row min-h-0 overflow-y-auto lg:overflow-y-hidden">
          
          {/* Panel Izquierdo: Escáner y Último Escaneo */}
          <div className="w-full lg:w-5/12 border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-800 flex flex-col bg-gray-50 dark:bg-[#121212] flex-shrink-0 lg:flex-shrink min-h-fit lg:min-h-0">
            
            {/* Cámara */}
            <div className="flex-shrink-0 p-4 lg:px-10 lg:pb-2 xl:px-14">
              <div className="bg-white dark:bg-[#1e1e1e] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="bg-gray-100 dark:bg-[#2a2a2a] px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <span className="relative flex h-3 w-3">
                      <span className={clsx(
                        "animate-ping absolute inline-flex h-full w-full rounded-full opacity-75",
                        scanning ? "bg-green-400" : cameraActive ? "bg-yellow-400" : "bg-gray-400"
                      )}></span>
                      <span className={clsx(
                        "relative inline-flex rounded-full h-3 w-3",
                        scanning ? "bg-green-500" : cameraActive ? "bg-yellow-500" : "bg-gray-500"
                      )}></span>
                    </span>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                      {!cameraActive ? 'Cámara Apagada' : scanning ? 'Cámara Activa' : 'Iniciando Cámara...'}
                    </span>
                  </div>
                  <button 
                    onClick={toggleCamera}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-colors bg-white hover:bg-gray-50 text-gray-700 border-gray-300 dark:bg-[#1a1a1a] dark:hover:bg-[#222] dark:text-gray-300 dark:border-gray-600"
                  >
                    {cameraActive ? 'Apagar Cámara' : 'Encender Cámara'}
                  </button>
                </div>
                
                {/* Contenedor del video - cuadrado para alinearse con aspectRatio:1 del scanner */}
                <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: '1 / 1' }}>
                  <div id="qr-reader-admin" className="w-full h-full" />
                  
                  {!cameraActive ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900 text-white z-10">
                      <QrCode size={64} className="mx-auto mb-3 opacity-30" />
                      <p className="text-lg font-medium text-gray-400">Cámara pausada</p>
                    </div>
                  ) : !scanning ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-900/90 text-white z-10">
                      <QrCode size={64} className="mx-auto mb-3 opacity-50 animate-pulse" />
                      <p className="text-lg font-medium">Iniciando escáner...</p>
                      <p className="text-sm opacity-70 mt-1">Por favor permita el acceso a la cámara</p>
                    </div>
                  ) : null}
                  
                  {scanning && (
                    <div className="absolute inset-0 pointer-events-none border-[3px] border-dashed border-white/30 m-8 rounded-2xl">
                      <div className="absolute -top-3 -left-3 w-8 h-8 border-t-4 border-l-4 border-teal-500 rounded-tl-xl"></div>
                      <div className="absolute -top-3 -right-3 w-8 h-8 border-t-4 border-r-4 border-teal-500 rounded-tr-xl"></div>
                      <div className="absolute -bottom-3 -left-3 w-8 h-8 border-b-4 border-l-4 border-teal-500 rounded-bl-xl"></div>
                      <div className="absolute -bottom-3 -right-3 w-8 h-8 border-b-4 border-r-4 border-teal-500 rounded-br-xl"></div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Resultado del último escaneo */}
            <div className="flex-1 lg:overflow-y-auto p-4 bg-gray-50 dark:bg-[#121212]">
              <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3 px-1">
                Último Escaneo
              </h3>
              
              {lastScan ? (
                <div className={clsx(
                  "rounded-xl shadow-md border overflow-hidden transition-all duration-300 transform",
                  lastScan.valido 
                    ? "bg-white dark:bg-[#1e1e1e] border-green-200 dark:border-green-900/50" 
                    : "bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-900/50"
                )}>
                  <div className={clsx(
                    "px-4 py-3 border-b flex justify-between items-center",
                    lastScan.valido 
                      ? "bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-900/30" 
                      : "bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-900/30"
                  )}>
                    <div className="flex items-center gap-2">
                      {lastScan.valido ? (
                        <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                      )}
                      <span className={clsx(
                        "font-bold uppercase tracking-wide",
                        lastScan.valido ? "text-green-800 dark:text-green-300" : "text-red-800 dark:text-red-300"
                      )}>
                        {lastScan.valido ? 'Acceso Permitido' : 'Acceso Denegado'}
                      </span>
                    </div>
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatTime(lastScan.timestamp)}
                    </span>
                  </div>
                  
                  <div className="p-5">
                    <div className="text-center mb-5">
                      {renderStatusIcon(lastScan.valido)}
                      <h4 className="text-xl font-bold text-gray-900 dark:text-white mt-3">
                        {lastScan.invitado}
                      </h4>
                      {!lastScan.valido && (
                        <p className="text-red-600 dark:text-red-400 text-sm font-medium mt-1">
                          {lastScan.error}
                        </p>
                      )}
                    </div>
                    
                    {lastScan.valido && (
                      <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded-lg p-4 border border-gray-100 dark:border-gray-800 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1 uppercase">
                              Boletos Totales
                            </div>
                            <div className="text-lg font-bold text-gray-900 dark:text-white">
                              {lastScan.boletosTotal}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1 uppercase">
                              Registrados ahora
                            </div>
                            <div className="text-lg font-bold text-teal-600 dark:text-teal-400">
                              +{lastScan.boletos}
                            </div>
                          </div>
                        </div>
                        {lastScan.mesas && (
                          <div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 font-medium mb-1 uppercase">Mesa(s)</div>
                            <div className="text-sm font-bold text-teal-600 dark:text-teal-400">{lastScan.mesas}</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="h-48 flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 bg-white dark:bg-[#1e1e1e] rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
                  <QrCode size={48} className="mb-3 opacity-30" />
                  <p>Esperando escaneo...</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Panel Derecho: Lista de invitados (Admin Mode) */}
          <div className="w-full lg:w-7/12 flex flex-col bg-white dark:bg-[#1a1a1a] flex-shrink-0 lg:flex-shrink min-h-fit lg:min-h-0">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 space-y-4">
              <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 flex flex-col xl:flex-row xl:items-center gap-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-casal dark:text-casal-400" />
                  Progreso del Evento
                </div>
                {(() => {
                  const totalBoletos = invitados.reduce((sum, inv) => sum + (inv.cantidad_boletos || 0), 0);
                  const boletosEscaneados = invitados.reduce((sum, inv) => sum + (inv.boletos_escaneados || 0), 0);
                  const graduadosCompletos = invitados.filter(inv => inv.boletos_escaneados > 0 && inv.boletos_escaneados === inv.cantidad_boletos).length;
                  const totalGraduados = invitados.length;
                  return (
                    <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm font-normal w-full sm:w-auto">
                      <span className="bg-gray-100 dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm text-center">
                        <strong className="text-casal dark:text-teal-400 font-bold">{boletosEscaneados}</strong> / {totalBoletos} Boletos
                      </span>
                      <span className="bg-gray-100 dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-300 px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm text-center">
                        <strong className="text-casal dark:text-teal-400 font-bold">{graduadosCompletos}</strong> / {totalGraduados} Graduados
                      </span>
                    </div>
                  );
                })()}
              </h3>
              
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Buscar por nombre o correo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-gray-50 dark:bg-[#232323] text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-casal focus:border-casal sm:text-sm transition-colors"
                />
              </div>
            </div>
            
            <div className="flex-1 lg:overflow-y-auto p-4 space-y-3">
              {(() => {
                const filtradosYOrdenados = invitados
                  .filter(inv => 
                    inv.invitado_nombre.toLowerCase().includes(searchTerm.toLowerCase()) || 
                    (inv.correo && inv.correo.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .sort((a, b) => a.invitado_nombre.localeCompare(b.invitado_nombre));

                if (filtradosYOrdenados.length === 0) {
                  return (
                    <div className="text-center py-10 text-gray-500">
                      {invitados.length === 0 
                        ? "No hay invitados confirmados para este evento." 
                        : "No se encontraron invitados que coincidan con la búsqueda."}
                    </div>
                  );
                }

                return filtradosYOrdenados.map(inv => {
                  const usados = inv.boletos_escaneados || 0;
                  const total = inv.cantidad_boletos;
                  const progress = total > 0 ? (usados / total) * 100 : 0;
                  const mesasStr = inv.mesas && Array.isArray(inv.mesas) && inv.mesas.length > 0
                    ? inv.mesas.map(m => m.mesa_numero).join(', ')
                    : null;
                
                return (
                  <div key={inv.id} className="bg-gray-50 dark:bg-[#232323] p-3 sm:p-4 rounded-xl border border-gray-100 dark:border-gray-800">
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-900 dark:text-white text-sm sm:text-base truncate">
                          {inv.invitado_nombre}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                          <Mail className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{inv.correo || "Sin correo"}</span>
                        </div>
                      </div>
                      <div className={clsx(
                        "flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full border",
                        usados === total && total > 0
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800"
                          : usados > 0
                          ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-800"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700"
                      )}>
                        {usados}/{total}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                          <div 
                            className={clsx(
                              "h-2 rounded-full transition-all duration-500",
                              usados === total && total > 0 ? "bg-green-500" : "bg-teal-500"
                            )}
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                      {mesasStr && (
                        <div className="flex-shrink-0 text-xs text-teal-600 dark:text-teal-400 font-semibold flex items-center gap-1">
                          <span className="opacity-60">Mesa:</span> {mesasStr}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })})()}
            </div>
          </div>

        </div>

        {/* Footer */}
        <div className="flex-shrink-0 bg-gray-50 dark:bg-[#121212] px-6 py-4 border-t border-gray-200 dark:border-gray-800">
          <div className="flex justify-between items-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              💡 <span className="font-medium">Tip:</span> El QR debe pertenecer a un invitado de este evento
            </p>
            <button
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 dark:bg-gray-700 dark:hover:bg-gray-600 text-white rounded-lg font-semibold transition-colors shadow-md"
              onClick={handleClose}
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Confirmación de Boletos */}
      {pendingConfirmation && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black/60 dark:bg-black/80 backdrop-blur-sm" onClick={procesandoEscaneo ? null : handleCancelarConfirmacion} />
          
          <div className="relative z-[71] mx-auto w-full max-w-sm rounded-xl bg-white dark:bg-[#1e1e1e] shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden animate-zoom-in">
            {/* Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 dark:from-teal-600 dark:to-teal-700 px-4 py-3">
              <h2 className="text-base font-bold text-white flex items-center gap-2">
                <Ticket className="w-5 h-5" />
                Registrar Escaneo
              </h2>
            </div>

            {/* Content */}
            <div className="px-4 py-4">
              {/* Información del Invitado */}
              <div className="bg-teal-50 dark:bg-teal-900/20 rounded-xl p-3 mb-4 border border-teal-100 dark:border-teal-900/50">
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-teal-100 dark:bg-teal-800/50 rounded-full flex items-center justify-center border border-teal-200 dark:border-teal-700">
                    <User className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-gray-900 dark:text-gray-100 text-sm leading-tight">
                      {pendingConfirmation.invitadoData.invitado_nombre}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1 truncate">
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{pendingConfirmation.invitadoData.correo}</span>
                    </div>
                  </div>
                </div>
                {/* Stats: 2 cols para boletos + fila completa para mesa */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="bg-white dark:bg-[#1a1a1a] px-2 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800 text-center">
                    <div className="text-gray-500 text-[10px] uppercase font-medium mb-0.5">Totales</div>
                    <div className="font-bold text-gray-900 dark:text-white text-sm">{pendingConfirmation.invitadoData.cantidad_boletos}</div>
                  </div>
                  <div className="bg-white dark:bg-[#1a1a1a] px-2 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800 text-center">
                    <div className="text-gray-500 text-[10px] uppercase font-medium mb-0.5">Restantes</div>
                    <div className="font-bold text-teal-600 dark:text-teal-400 text-sm">{pendingConfirmation.boletosRestantes}</div>
                  </div>
                </div>
                <div className="bg-white dark:bg-[#1a1a1a] px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-800 flex items-center justify-between gap-2">
                  <div className="text-gray-500 text-[10px] uppercase font-medium flex-shrink-0">Mesa(s)</div>
                  <div className="font-bold text-teal-600 dark:text-teal-400 text-xs text-right break-all">
                    {pendingConfirmation.mesas || 'Sin asignar'}
                  </div>
                </div>
              </div>

              {/* Selector de Boletos */}
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-2 text-center">
                    ¿Cuántos boletos desea descontar?
                  </label>
                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={() => setBoletosConfirmar(Math.max(1, boletosConfirmar - 1))}
                      disabled={boletosConfirmar <= 1 || procesandoEscaneo}
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800 rounded-full font-bold text-xl text-gray-700 dark:text-gray-300 transition-colors shadow-sm"
                    >
                      −
                    </button>
                    
                    <div className="text-center w-20">
                      <input 
                        type="number"
                        min="1"
                        max={pendingConfirmation.boletosRestantes}
                        value={boletosConfirmar === '' ? '' : boletosConfirmar}
                        onChange={(e) => {
                          if (e.target.value === '') {
                            setBoletosConfirmar('');
                            return;
                          }
                          const val = parseInt(e.target.value);
                          if (!isNaN(val)) {
                            setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, Math.max(1, val)));
                          }
                        }}
                        onBlur={() => {
                          if (boletosConfirmar === '' || boletosConfirmar < 1) {
                            setBoletosConfirmar(1);
                          }
                        }}
                        className="w-full text-4xl font-black text-gray-900 dark:text-white bg-transparent text-center focus:outline-none focus:ring-2 focus:ring-teal-500 rounded-lg p-1 hide-arrows"
                      />
                    </div>

                    <button
                      onClick={() => setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, (boletosConfirmar || 0) + 1))}
                      disabled={boletosConfirmar >= pendingConfirmation.boletosRestantes || procesandoEscaneo}
                      className="w-11 h-11 flex items-center justify-center bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:hover:bg-gray-100 dark:disabled:hover:bg-gray-800 rounded-full font-bold text-xl text-gray-700 dark:text-gray-300 transition-colors shadow-sm"
                    >
                      +
                    </button>
                  </div>
                  
                  {/* Botones rápidos */}
                  {pendingConfirmation.boletosRestantes > 1 && (
                    <div className="flex justify-center gap-2 mt-3 flex-wrap">
                      <button 
                        onClick={() => setBoletosConfirmar(pendingConfirmation.boletosRestantes)}
                        className="px-3 py-1 bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300 rounded-full text-xs font-bold hover:bg-teal-200 dark:hover:bg-teal-800/60 transition-colors border border-teal-200 dark:border-teal-800"
                      >
                        Todos ({pendingConfirmation.boletosRestantes})
                      </button>
                      {pendingConfirmation.boletosRestantes >= 5 && (
                        <button 
                          onClick={() => setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, (boletosConfirmar || 0) + 5))}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                        >
                          +5
                        </button>
                      )}
                      {pendingConfirmation.boletosRestantes >= 10 && (
                        <button 
                          onClick={() => setBoletosConfirmar(Math.min(pendingConfirmation.boletosRestantes, (boletosConfirmar || 0) + 10))}
                          className="px-3 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 rounded-full text-xs font-bold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
                        >
                          +10
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Botones de acción */}
                <div className="flex gap-2 mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
                  <button
                    onClick={handleCancelarConfirmacion}
                    disabled={procesandoEscaneo}
                    className="flex-1 px-3 py-2.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-bold text-sm transition-colors disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleConfirmarBoletos}
                    disabled={procesandoEscaneo || boletosConfirmar === '' || boletosConfirmar < 1}
                    className="flex-1 px-3 py-2.5 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold text-sm transition-colors shadow-md shadow-teal-500/30 disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {procesandoEscaneo ? <InlineSpinner size="sm" className="text-white" /> : null}
                    Confirmar {boletosConfirmar !== '' ? boletosConfirmar : 0}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Users(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function Mail(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
    </svg>
  );
}
